// Hard ceilings, independent of any plan. They exist so the reads that list
// these things can be bounded without ever silently dropping a row: creation
// refuses at the ceiling, so a `.take(MAX)` always returns everything.
export const MAX_WORKSPACES_PER_USER = 20;
export const MAX_MEMBERS_PER_WORKSPACE = 200;
export const MAX_PROJECTS_PER_WORKSPACE = 100;
export const MAX_PENDING_INVITATIONS = 100;
// Keys that work. Revoked keys are kept for the record, up to the second
// number, after which the oldest of them are forgotten.
export const MAX_API_KEYS_PER_PROJECT = 20;
export const MAX_API_KEY_ROWS = 100;

// A person's agent tokens in one workspace: how many can work at once, and
// how many rows (revoked ones included) are kept.
export const MAX_AGENT_TOKENS_PER_USER = 20;
export const MAX_AGENT_TOKEN_ROWS = 100;
/** Agent accounts a workspace keeps, and how many of them one person may own. */
export const MAX_AGENTS_PER_WORKSPACE = 50;
export const MAX_AGENTS_PER_OWNER = 10;
// Removed agents are kept, so what they did keeps its name: every row,
// removed ones included, up to this.
export const MAX_AGENT_ROWS_PER_WORKSPACE = 200;
// Agents one person can make in a day.
export const AGENTS_CREATED_PER_DAY = 10;
/** Reply drafts waiting on one ticket: one per agent, and no more than this. */
export const MAX_REPLY_DRAFTS_PER_TICKET = 10;
// Requests one token may make in a minute. An agent at work makes a few
// calls a second in bursts; a loop gone wrong is stopped here.
export const AGENT_RATE_PER_MINUTE = 120;
// How much of a list one tool call returns, and what an agent is given of a
// conversation: enough to work with, small enough to read.
export const AGENT_PAGE_DEFAULT = 25;
export const AGENT_PAGE_MAX = 50;
export const AGENT_THREAD_MAX = 30;
// The body of one request to the agent endpoint: the longest text a tool
// takes, with room for the rest. That is a diagram's body
// (DIAGRAM_BODY_MAX), six bytes a character in the worst case: JSON that
// writes every letter outside ASCII as \uXXXX.
export const AGENT_MAX_BODY_BYTES = 1280 * 1024;
// Messages in one batch. Each can be a tool call, and they all count as one
// request against the rate.
export const AGENT_MAX_BATCH = 10;
// Days an agent's summary of Work can go back (seven unless it says), and the
// issues it names, worked on most first. Within what the journal keeps.
export const AGENT_WORK_DAYS_DEFAULT = 7;
export const AGENT_WORK_DAYS_MAX = 31;
export const AGENT_WORK_SUMMARY_ISSUES = 20;
// What get_project shows of a template's description: enough to tell what
// the template is for. The whole of it goes into the issue.
export const AGENT_TEMPLATE_DESCRIPTION_SHOWN = 280;

export const MAX_ALLOWED_ORIGINS = 20;
// The picture of a workspace or of a project. It is only ever shown small.
export const MAX_IMAGE_BYTES = 2 * 1024 * 1024;
// What an <img> shows everywhere and cannot carry a script, which leaves SVG
// out: the browser draws an SVG (or an ICO) into a PNG before it uploads it.
export const IMAGE_TYPES = ["image/png", "image/jpeg", "image/webp", "image/gif"];
// The pictures of a project's repository offered as its picture: so many,
// and no more bytes than this in all, which is what the browser downloads.
export const MAX_REPO_IMAGES = 6;
export const MAX_REPO_IMAGE_TOTAL_BYTES = 4 * 1024 * 1024;
// Searches of a project's repository for its picture in ten minutes.
export const REPO_IMAGE_SEARCHES_PER_TEN_MINUTES = 20;
// Screenshots and other pictures a teammate attaches to a ticket they write.
// A full-screen capture on a high-density display is a few megabytes.
export const MAX_TICKET_IMAGES = 10;
export const MAX_TICKET_IMAGE_BYTES = 10 * 1024 * 1024;
// The same go on a reply or a note, but no more bytes than this in all: a
// reply's pictures travel with its email as attachments, and Resend takes 40
// MB an email once they are base64, which makes a third more of them.
export const MAX_MESSAGE_IMAGE_TOTAL_BYTES = 25 * 1024 * 1024;

// ---- Tickets ----
// Tickets one bulk action may touch, which is also the most a notification
// bulk action takes. The page selects at most this many.
export const MAX_BULK_ITEMS = 100;
// Rows a list page may ask for, whatever the client sends.
export const MAX_PAGE_SIZE = 100;
// What one page of a list may read before it hands back what it has. A page
// can outgrow what was asked for (rows arriving inside it, or a filter that
// turns most rows away); past these the answer is cut short and the client
// splits the page. See lib/pagination.ts.
export const MAX_PAGE_ROWS_READ = 500;
export const MAX_PAGE_BYTES_READ = 4 * 1024 * 1024;
// The same ceiling for a list whose rows each bring a whole ticket with them
// (a label's links, the inbox). Such rows are small, so the byte ceiling,
// which only weighs the rows themselves, never stops the page; the tickets
// behind them can be 50 KB each at the most ingestion lets through. This many
// of those is still well under what one query may read. It is above
// MAX_PAGE_SIZE, so a page that is merely full never reaches it.
export const MAX_JOINED_ROWS_READ = 120;
export const MAX_LABELS_PER_PROJECT = 100;
export const MAX_LABELS_PER_TICKET = 20;
// Labels added or removed by one call, counted across all its tickets. Each
// is two writes (the link and its entry in the log), so this is what keeps a
// bulk relabelling well inside what one transaction may write.
export const MAX_LABEL_CHANGES = 1000;
export const LABEL_NAME_MAX = 40;
export const MAX_SAVED_VIEWS_PER_PROJECT = 50;
// Values one kind of filter may list (assignees, labels).
export const MAX_FILTER_VALUES = 20;
// A note is as long as a ticket's own message may be.
export const MAX_NOTE_LENGTH = 10_000;
// A ticket a teammate writes by hand: its title, and its requester's name.
export const TICKET_TITLE_MAX = 256;
export const CONTACT_NAME_MAX = 255;
// A snooze ends within a year.
export const MAX_SNOOZE_MS = 366 * 24 * 60 * 60 * 1000;
// Tickets shown as duplicates of one ticket, and re-pointed per transaction
// when the ticket they duplicate turns out to be a duplicate itself.
export const MAX_DUPLICATES_LISTED = 50;
export const DUPLICATE_REPOINT_BATCH = 100;
// How far a chain of duplicates is followed when looking for a loop. Chains
// are kept one link long, so this only bounds the walk over damaged data.
export const MAX_DUPLICATE_CHAIN = 10;
// A requester's other tickets, beside the ticket being read.
export const MAX_CONTACT_TICKETS = 10;
// Tickets offered as the one to merge a duplicate into.
export const MAX_DUPLICATE_CANDIDATES = 20;
// What is read to find that many: the requester's newest tickets, and the
// best matches of a search. More than are shown, because the ticket itself
// and tickets that are already duplicates are among them and are left out.
export const CONTACT_CANDIDATE_WINDOW = 40;
export const SEARCH_CANDIDATE_WINDOW = 100;
// Text typed into a search box, in characters. Longer is cut, not refused.
export const MAX_SEARCH_LENGTH = 200;
// Words of it handed to a search index, which takes no more than this many.
export const MAX_SEARCH_TERMS = 16;
// The unread badge says "99+" past this, so nothing ever counts them all.
export const UNREAD_COUNT_CAP = 99;
// Unread rows the badge goes through to find those, past the ones of projects
// the reader has lost: however many of those there are, it stops here.
export const MAX_UNREAD_SCANNED = 1000;
// Rows a background sweep (a deleted label's links, "mark all read") writes
// per transaction before it schedules itself again.
export const SWEEP_BATCH = 200;

// ---- Issues ----
// GitHub's limits, held here too so that an issue written in Echoes can
// always be written there: a title, and a description or a comment.
export const ISSUE_TITLE_MAX = 256;
export const ISSUE_BODY_MAX = 65_536;
// The longest an estimate can be: four weeks of eight-hour days.
export const ISSUE_ESTIMATE_MAX_MINUTES = 4 * 5 * 8 * 60;
// A chain of sub-issues, counting the issue at the top, and the sub-issues
// one issue may have. GitHub's as well.
export const MAX_ISSUE_DEPTH = 8;
export const MAX_SUB_ISSUES = 100;
// Issues read below an issue that is being given a parent, to find how deep
// its own sub-issues go. Past this the move is refused as too deep.
export const MAX_SUBTREE_READ = 500;
// Relations one issue may have to others, counting both ends: what it
// blocks, what blocks it, its duplicates and what it is related to.
export const MAX_RELATIONS_PER_ISSUE = 50;
// People subscribed to one issue. Each is told of every comment and every
// change of status or assignee, in a transaction of its own per change
// (lib/issueSubscriptions.ts), so this is also the most one change writes.
export const MAX_SUBSCRIBERS_PER_ISSUE = 100;
// GitHub's limit on the labels of one issue.
export const MAX_LABELS_PER_ISSUE = 100;
export const MAX_ISSUE_SEARCH_RESULTS = 20;
// People one comment may name (@mentions).
export const MAX_MENTIONS_PER_COMMENT = 20;
export const MAX_PULL_REQUESTS_LISTED = 20;
// What a team creates by hand. Milestones and types that arrive from GitHub
// are not held to these, so their lists paginate or read past them.
export const MAX_OPEN_MILESTONES_PER_PROJECT = 100;
export const MILESTONE_TITLE_MAX = 255;
export const MILESTONE_DESCRIPTION_MAX = 2000;
export const MAX_ISSUE_TYPES_PER_PROJECT = 25;
export const ISSUE_TYPE_NAME_MAX = 40;
export const ISSUE_TYPE_DESCRIPTION_MAX = 200;
// Templates a project's new issues can start from, and the sub-issues one
// template lists. A template's title, description and sub-issue titles are
// an issue's, held to the issue limits above; its labels to an issue's too.
export const MAX_ISSUE_TEMPLATES_PER_PROJECT = 25;
export const MAX_TEMPLATE_SUB_ISSUES = 20;
export const ISSUE_TEMPLATE_NAME_MAX = 60;
// The distance left between two issues placed one after the other, and the
// distance below which two neighbours are spread out again before a third is
// dropped between them. See lib/issueOrder.ts.
export const SORT_ORDER_STEP = 1024;
export const SORT_ORDER_MIN_GAP = 1e-6;
export const SORT_REBALANCE_WINDOW = 50;

// ---- Roadmap ----
// Items that are planned or in progress. A single query reads all of them
// for the board, and another for the public page, so creation refuses here.
export const MAX_OPEN_ROADMAP_ITEMS = 100;
// Shipped items the board and the public page show, newest first. Older ones
// are kept and are not listed.
export const SHIPPED_ROADMAP_ITEMS_SHOWN = 50;
export const ROADMAP_TITLE_MAX = 120;
export const ROADMAP_DESCRIPTION_MAX = 5000;
// Issues one roadmap item may be linked to, all of them listed beside it.
export const MAX_ISSUES_PER_ROADMAP_ITEM = 50;
// Votes one voter may cast or take back in a minute, and voter tokens one
// address may be handed in an hour. See convex/portal.ts.
export const VOTES_PER_VOTER_PER_MINUTE = 30;
export const VOTER_TOKENS_PER_ADDRESS_PER_HOUR = 20;

// ---- Architecture ----
// The bricks and the connections of one project's diagram. A single query
// reads all of them with everything they hold, so these two numbers and the
// two weights below are what bound that read: a hundred bricks of 24 KB and
// two hundred connections of 6 KB are 3.6 MB at the very most.
export const MAX_BRICKS_PER_PROJECT = 100;
export const MAX_CONNECTIONS_PER_PROJECT = 200;
export const BRICK_NAME_MAX = 64;
// What a brick does in this app, its kind and the frame it is drawn in, and
// what is written on a connection.
export const BRICK_ROLE_MAX = 80;
export const BRICK_KIND_MAX = 40;
export const BRICK_GROUP_MAX = 40;
export const CONNECTION_LABEL_MAX = 40;
// What a brick and a connection may hold beyond that, in bytes once stored.
// Bytes and not characters: a character can take four.
export const BRICK_DETAILS_MAX_BYTES = 24 * 1024;
export const CONNECTION_DETAILS_MAX_BYTES = 6 * 1024;
// One line of a list (a route, a job, a path, an address) and one short
// value (a package, an account, the name of a variable or of where it is set).
export const DETAIL_LINE_MAX = 160;
export const DETAIL_SHORT_MAX = 80;
// What a connection carries, how, and what comes back.
export const DETAIL_TEXT_MAX = 300;
// Lines one list may have, and entries of the kinds that are pairs
// (environments, places configuration is set, links).
export const MAX_DETAIL_LINES = 60;
export const MAX_DETAIL_GROUPS = 8;
export const ARCHITECTURE_NOTES_MAX = 4000;
export const LINK_URL_MAX = 500;
// How far from the origin a brick may be put, in pixels each way.
export const CANVAS_EXTENT = 20_000;
// What one scan of a project's repositories reads: so many repositories, so
// many of each one's paths, and so many of its files, none larger than a
// manifest ever is. Each file is one call to GitHub.
export const MAX_SCAN_REPOS = 3;
export const MAX_SCAN_TREE_PATHS = 50_000;
export const MAX_SCAN_FILES = 48;
export const MAX_SCAN_FILE_BYTES = 256 * 1024;
// The files of code a scan reads for what they import, forty to a call: so
// many, none larger than this, and no more than this in all.
export const MAX_SCAN_SOURCES = 240;
export const MAX_SCAN_SOURCE_BYTES = 120 * 1024;
export const MAX_SCAN_SOURCE_TOTAL_BYTES = 3 * 1024 * 1024;
// What a scan may suggest, and how many places it names for each suggestion.
// Kept on one document with everything each holds, which these bound.
export const MAX_FOUND_BRICKS = 40;
export const MAX_FOUND_CONNECTIONS = 80;
export const MAX_FOUND_EVIDENCE = 6;
// Scans one project may be asked for by hand in ten minutes.
export const SCANS_PER_TEN_MINUTES = 6;
// A scan that has not finished after this long is taken to have been lost,
// and another may be asked for.
export const SCAN_LOST_AFTER_MS = 3 * 60_000;
// After a push, a scan waits this long for the pushes that follow it, and
// one project is not read again sooner than this after its last scan.
export const SCAN_AFTER_PUSH_MS = 60_000;
export const SCAN_PUSH_INTERVAL_MS = 10 * 60_000;
// Bricks of one project the workspace's Stack reads.
export const MAX_STACK_BRICKS_PER_PROJECT = 60;

// ---- GitHub ----
export const MAX_GITHUB_INSTALLATIONS_PER_WORKSPACE = 20;
export const MAX_GITHUB_REPOS_PER_PROJECT = 20;
// A trip to GitHub and back (installing the app, linking an account) has
// this long to return.
export const GITHUB_OAUTH_STATE_TTL_MS = 10 * 60_000;
// GitHub delivers up to 25 MB. Nothing the mirror follows comes near this,
// and a larger delivery is refused unread.
export const GITHUB_WEBHOOK_MAX_BYTES = 3 * 1024 * 1024;
// Options one single-select field of a GitHub Project may bring. GitHub
// itself stops at fifty.
export const MAX_GITHUB_FIELD_OPTIONS = 100;
// Repositories one installation event may name before the rest is left to
// the periodic catch-up.
export const MAX_GITHUB_EVENT_REPOS = 500;
// Failures in a row after which a push stops retrying and shows its error.
export const MAX_GITHUB_PUSH_ATTEMPTS = 8;
// How long a push may hold the claim on creating an issue or sending a
// comment. Longer than an action can run, so a claim older than this was
// left by a push that died.
export const GITHUB_CLAIM_TTL_MS = 15 * 60_000;
// The same for one comment, whose push is a single call.
export const GITHUB_COMMENT_CLAIM_TTL_MS = 2 * 60_000;
// An import that has not moved for this long has stopped.
export const GITHUB_IMPORT_STALE_MS = 30 * 60_000;
// A push scheduled this long ago and still owed did not finish.
export const GITHUB_PUSH_STALE_MS = 20 * 60_000;
// The length an error shown on an issue is cut to.
export const GITHUB_ERROR_MAX = 200;
// Issues of one page from GitHub stored per transaction.
export const GITHUB_IMPORT_CHUNK = 25;
// Pages of an issue's existing comments read the first time it is opened.
export const MAX_GITHUB_COMMENT_PAGES = 20;
// Issues `pushExisting` claims per transaction, and the pause between two of
// their creations: GitHub slows down an app that creates content in bursts.
export const GITHUB_PUSH_EXISTING_BATCH = 50;
export const GITHUB_CREATE_SPACING_MS = 1_000;
// The tickets one pull request opens: the steps of a project's workflow.
export const MAX_WORKFLOW_STEPS = 10;
export const WORKFLOW_STEP_TITLE_MAX = 80;
// Identifiers and closing references read from one pull request.
export const MAX_PULL_REQUEST_ISSUES = 20;
// Pages of fifty pull requests, newest first, read from a repository the
// first time: a thousand is as far back as the page goes.
export const MAX_PULL_REQUEST_IMPORT_PAGES = 20;
// Issues shown beside one pull request in the list.
export const MAX_PULL_REQUEST_ISSUES_SHOWN = 5;
// Open pull requests counted for the page's heading; past this it says "99+".
export const MAX_PULL_REQUESTS_COUNTED = 100;
// Connected repositories the catch-up looks at per transaction.
export const GITHUB_CATCH_UP_BATCH = 100;
// ---- Slack ----
// A trip to Slack and back (adding the app to a Slack workspace) has this
// long to return.
export const SLACK_OAUTH_STATE_TTL_MS = 10 * 60_000;
// What one request from Slack may weigh. A message shortcut carries the whole
// message, which Slack lets run to 40 000 characters, with its blocks.
export const SLACK_REQUEST_MAX_BYTES = 1024 * 1024;
// How far a request's timestamp may be from now, in seconds, before it is
// taken for a replay. Slack's own advice.
export const SLACK_TIMESTAMP_TOLERANCE_S = 5 * 60;
// How long a call to Slack may take. Slack gives a shortcut three seconds to
// open its form, so a call made while it waits gets less.
export const SLACK_TIMEOUT_MS = 10_000;
// What a request from Slack may spend in all before it is answered, the
// calls it makes to Slack included: Slack's three seconds, less a margin.
export const SLACK_INTERACTIVE_BUDGET_MS = 2_600;
// How long a form that created an issue is remembered, so that sending it
// again gives the same issue. Longer than a signed request stays good.
export const SLACK_SUBMISSION_TTL_MS = 60 * 60_000;
// Pages of two hundred channels read for a project's channel picker. Past
// a thousand, a channel is found by its id.
export const MAX_SLACK_CHANNEL_PAGES = 5;
export const SLACK_CHANNEL_PAGE_SIZE = 200;
// Channel lists one workspace may ask Slack for per minute: Slack itself
// allows twenty.
export const SLACK_CHANNEL_LISTS_PER_MINUTE = 10;
export const SLACK_CHANNEL_NAME_MAX = 80;
// Messages one channel is sent: this many at once, earned back at this many
// an hour. A bulk change of fifty issues posts the first twenty-five; past
// it, a post is dropped rather than queued. Slack takes about one a second
// per channel and slows down an app that sends more.
export const SLACK_POSTS_BURST = 25;
export const SLACK_POSTS_PER_CHANNEL_PER_HOUR = 300;
// Attempts at one post that Slack asked to slow down.
export const SLACK_POST_MAX_ATTEMPTS = 3;
// Requests one Slack workspace may make of Echoes in a minute (opening the
// form, sending it), and issues one person may create from Slack in an hour.
export const SLACK_REQUESTS_PER_TEAM_PER_MINUTE = 120;
export const SLACK_ISSUES_PER_USER_PER_HOUR = 30;
// The projects the form offers: Slack's most for one menu.
export const MAX_SLACK_PROJECT_OPTIONS = 100;
// What a text field of a Slack form holds: Slack's most.
export const SLACK_TEXT_INPUT_MAX = 3000;
// How long a form's fields are, for Slack's menu and its labels.
export const SLACK_OPTION_TEXT_MAX = 75;

// ---- Webhooks ----
// Endpoints per project. Kept low on purpose: an event is one stored body and
// one scheduled run for each endpoint that listens, written by the mutation
// the event happened in, and a transaction may write 16 MiB.
//
// Four is the most that fits, measured by webhooks.bulk.test.ts through the
// real engine: a decline of MAX_BULK_ITEMS of the heaviest tickets, with the
// longest reply MAX_BULK_REPLY_CHARS leaves, every character of it one JSON
// writes as six bytes (a control character). That writes 15.1 MiB with four
// endpoints listening and 16.7 with five, which Convex refuses. (An ordinary
// reply of that length writes 11.3 and 11.9; one of three-byte characters
// 13.2 and 14.2.) Each endpoint is about 1.6 MiB of that at the worst. Raise
// this, MAX_BULK_ITEMS or MAX_BULK_REPLY_CHARS only with that test passing.
export const MAX_WEBHOOKS_PER_PROJECT = 4;
// A longer URL is refused, not cut.
export const WEBHOOK_URL_MAX = 2048;
// When a delivery that was not answered with a 2xx is tried again, each wait
// counted from the start of the attempt before it: 1 minute, 5 minutes, 30
// minutes, 3 hours, 20 hours. With the first attempt that makes six, the last
// one 23 hours 36 minutes after the event; after it the delivery has failed.
export const WEBHOOK_RETRY_DELAYS_MS = [
  60_000,
  5 * 60_000,
  30 * 60_000,
  3 * 60 * 60_000,
  20 * 60 * 60_000,
];
export const WEBHOOK_MAX_ATTEMPTS = WEBHOOK_RETRY_DELAYS_MS.length + 1;
// How long an endpoint has to answer: the headers, and what is read after them.
export const WEBHOOK_TIMEOUT_MS = 15_000;
// An attempt that has said nothing after this long never will (its action
// died). Only the last attempt needs it: before that, the next one is the cure.
export const WEBHOOK_STALL_MS = 5 * 60_000;
// What is read of an answer that was not a 2xx, in bytes, and what the
// delivery log keeps of it, in characters. A 2xx is not read at all.
export const WEBHOOK_RESPONSE_READ_MAX = 2048;
export const WEBHOOK_DETAIL_MAX = 300;
// How long the delivery log goes back.
export const WEBHOOK_DELIVERY_RETENTION_MS = 30 * 24 * 60 * 60_000;
// Deliveries a sweep (a removed webhook's log, the daily pruning) deletes per
// transaction. Fewer than SWEEP_BATCH: each row carries the body it sent.
export const WEBHOOK_DELIVERY_SWEEP_BATCH = 100;
// Test events and deliveries sent again by hand, per project per minute.
export const WEBHOOK_MANUAL_SENDS_PER_MINUTE = 20;
// Events a project's webhooks are told about: this many at once, earned back
// at this many an hour. Ten bulk actions of MAX_BULK_ITEMS back to back fit,
// and after them an event every 3.6 seconds for as long as it lasts: more
// than a team does by hand. It is there because every event is a stored body and up
// to WEBHOOK_MAX_ATTEMPTS runs of WEBHOOK_TIMEOUT_MS for each endpoint, on a
// scheduler the whole deployment shares, and anyone who may reply can cause
// them. Past it an event is logged for each endpoint as failed (`throttled`),
// without its body, and nothing is sent. An event fired by the API rather
// than by people will need a number of its own.
export const WEBHOOK_EVENTS_PER_PROJECT_PER_HOUR = 1_000;
// Deliveries in a row that ended failed before Echoes turns a webhook off
// itself. One that succeeds starts the count again.
export const WEBHOOK_BREAKER_FAILURES = 10;

// ---- Replies ----
// A reply is as long as a note (MAX_NOTE_LENGTH). When one decline sends the
// same reply to many tickets, its length times the number of tickets may not
// pass this: every ticket stores its own copy and hands another to each
// webhook, in the one transaction that also rewrites the tickets. A hundred
// tickets leave 2 000 characters each, and twenty the full length.
//
// Measured at the worst (MAX_BULK_ITEMS tickets as heavy as ingestion lets
// one be, the heaviest reply this allows, MAX_WEBHOOKS_PER_PROJECT endpoints
// listening) by webhooks.bulk.test.ts, with nothing mocked but the network:
// 15.1 MiB written and 13.7 read of the 16 a transaction may of each, and 600
// functions scheduled of the 1 000 (two per reply, one per endpoint). Raise
// this, the bulk size or the endpoints only with those numbers in hand.
export const MAX_BULK_REPLY_CHARS = 200_000;
// What is quoted of the requester's own message under a reply sent by email.
export const REPLY_QUOTE_LENGTH = 500;
// Replies a workspace may email, whoever writes them and whatever the
// project: this many at once, earned back at this many an hour. Far above
// what a team answers by hand or declines in bulk. It is there so that a
// workspace cannot be made into a way of sending mail at will, to addresses
// it chose itself through its own API key, under a name it chose. Past it a
// reply is stored and its webhooks told as always, and no email goes
// (`not_sent`, `rateLimited`).
export const EMAILED_REPLIES_PER_WORKSPACE_PER_HOUR = 1_000;
// The same for one person, across every workspace they belong to: someone
// can own MAX_WORKSPACES_PER_USER of them, and the limit above is each
// workspace's own. Above what anyone writes or declines in an hour.
export const EMAILED_REPLIES_PER_MEMBER_PER_HOUR = 500;
// And for one address, whoever writes to it and from whatever workspace: no
// one receives more replies from Echoes than this. A conversation by email
// is a few messages an hour; this is several times that.
export const EMAILED_REPLIES_PER_RECIPIENT_PER_HOUR = 20;
// Answers by email one ticket takes: this many at once, earned back at this
// many a day. A requester never writes that many. Whoever holds a reply
// address could, and each is a message, a notification and a call to Resend.
// Past it an email is dropped.
export const INBOUND_ANSWERS_PER_TICKET_PER_DAY = 30;
// When an email Resend received is asked for again after an attempt that did
// not end well, each wait counted from the start of the attempt before it: 1
// minute, 5 minutes, 30 minutes, 3 hours, 20 hours. With the first attempt
// that makes six, the last one 23 hours 36 minutes after the email arrived;
// after it the email is given up on. Long enough to put right an API key
// that may send and not read.
export const INBOUND_RETRY_DELAYS_MS = [
  60_000,
  5 * 60_000,
  30 * 60_000,
  3 * 60 * 60_000,
  20 * 60 * 60_000,
];
// How long the Resend component keeps an email, with its HTML and its text:
// a week after it was delivered, bounced or failed, so that a complaint or a
// late bounce still finds it, and a month when it never got that far (no
// webhook is set up, and `sent` is all that is ever known).
export const SENT_EMAIL_RETENTION_MS = 7 * 24 * 60 * 60_000;
export const UNSETTLED_EMAIL_RETENTION_MS = 30 * 24 * 60 * 60_000;
// What is read of a request to Resend's webhook, in bytes, before anything
// has shown who sent it. Resend's events are a few kilobytes; a longer body
// is answered like a bad signature and never held in memory.
export const RESEND_WEBHOOK_MAX_BYTES = 256 * 1024;
// How long Resend has to hand over an email it received, body included, and
// how much of its answer is read. An attempt that takes longer is one that
// failed, and is made again later; an email that is larger is given up on,
// since it would be as large the next time.
export const RECEIVED_EMAIL_TIMEOUT_MS = 15_000;
export const RECEIVED_EMAIL_MAX_BYTES = 5 * 1024 * 1024;

// ---- Changelog ----
export const CHANGELOG_TITLE_MAX = 200;
// Under what GitHub takes for a release's notes (125,000 characters).
export const CHANGELOG_BODY_MAX = 50_000;
export const CHANGELOG_VERSION_MAX = 100;
// Issues and pull requests one entry lists.
export const MAX_CHANGELOG_ITEMS = 200;
// Merged pull requests looked through for the ones no entry has listed yet.
export const MAX_UNRELEASED_PULL_REQUESTS_READ = 200;
// Drafts looked through for the date of the last release GitHub knows of.
export const MAX_CHANGELOG_DRAFTS_READ = 100;
// Releases a repository already has that are read when it is first seen:
// pages of a hundred, newest first.
export const MAX_RELEASE_IMPORT_PAGES = 2;
// Drafts that came from GitHub published in one go.
export const MAX_CHANGELOG_PUBLISH_BATCH = 200;
// How far ahead an entry can be scheduled.
export const CHANGELOG_SCHEDULE_MAX_MS = 366 * 24 * 60 * 60_000;

// ---- Work ----
// Machines one member reports from in one workspace: tokens that work, and
// the rows kept in all, revoked ones included (the oldest are forgotten).
export const MAX_WORK_TOKENS_PER_MEMBER = 10;
export const MAX_WORK_TOKEN_ROWS = 30;
// Worktrees one machine reports. A report names all of them at once, and is
// stored in one transaction: what is past this is refused, not cut, so that
// a worktree missing from a report always means it is gone.
export const MAX_WORK_ITEMS = 400;
// What is read of a report, in bytes. MAX_WORK_ITEMS of the longest
// worktree fit in it with their counts; the commits and files of each are
// what fills the rest, and the reporter leaves those out of the worktrees
// touched longest ago until the report fits (public/echoes-work.mjs).
export const WORK_BODY_MAX_BYTES = 2 * 1024 * 1024;
export const WORK_PATH_MAX = 400;
export const WORK_REPO_MAX = 140;
export const WORK_REF_MAX = 255;
export const WORK_SUBJECT_MAX = 200;
// What a report says of one worktree beyond its counts: the commits of its
// branch, newest first, its files not committed, and the files its branch
// changes. Past these the counts still say how many there are.
export const WORK_COMMITS_MAX = 50;
export const WORK_FILES_MAX = 100;
// Commit subjects a journal line keeps of the commits it counts.
export const WORK_EVENT_SUBJECTS_MAX = 10;
// The history of one worktree its panel reads.
export const WORK_HISTORY_MAX = 40;
// Days the summary of the journal goes back, and what of the workspace's
// activity in them it reads at most. A row is one worktree's day.
export const WORK_SUMMARY_DAYS = 7;
// The branches the summary names, worked on most first.
export const WORK_SUMMARY_BRANCHES = 8;
export const MAX_WORK_ACTIVITY_READ = 3_000;
// What one report counts toward a worktree's time when something changed in
// it: the time since the report before, never more than the interval the
// reporter keeps. Reports sent by hand in between do not add up to more.
export const WORK_REPORT_INTERVAL_MS = 15 * 60_000;
// Reports one machine may send a minute. The reporter sends one every
// quarter of an hour; the rest is room for running it by hand.
export const WORK_SYNCS_PER_MINUTE = 12;
// How long the journal goes back, and how much of it a sweep deletes per
// transaction.
export const WORK_EVENT_RETENTION_MS = 90 * 24 * 60 * 60_000;
// Worktrees with work in them the Work page reads: of each project the
// reader can open, and of their own that are in none. Past either the page
// says there are more.
export const MAX_WORK_ROWS_PER_PROJECT = 200;
export const MAX_WORK_ROWS_UNLINKED = 400;
// Lines one machine may add to the journal in a day. A day of real work is
// a few dozen. It is there because a report can name MAX_WORK_ITEMS
// worktrees that were not in the one before, and each would be a line.
export const WORK_EVENTS_PER_MACHINE_PER_DAY = 500;
// A machine that has said nothing for this long is taken to be gone: what it
// last reported is forgotten, and its next report starts over.
export const WORK_MACHINE_SILENT_MS = 30 * 24 * 60 * 60_000;
// Silent machines forgotten per run of the daily sweep.
export const WORK_SILENT_MACHINES_BATCH = 10;
// Pull requests of one project read, open and merged each, to say which
// branch already has one.
export const MAX_WORK_PULL_REQUESTS_READ = 100;
// Actions (convex/workActions.ts). A machine that takes them asks every
// minute; it is said to be listening while it has asked within
// WORK_LISTENING_MS, and when it asked is written down at most every
// WORK_LISTEN_REFRESH_MS.
export const WORK_LISTENING_MS = 10 * 60_000;
export const WORK_LISTEN_REFRESH_MS = 4 * 60_000;
// Times one machine may ask a minute: once from its schedule, the rest by hand.
export const WORK_ACTION_POLLS_PER_MINUTE = 20;
// Actions one member may ask for in an hour, across their machines.
export const WORK_ACTIONS_PER_HOUR = 120;
// Actions handed to a machine at once, and answers taken from it at once.
export const WORK_ACTIONS_PER_POLL = 20;
// What is read of a machine's answers, in bytes.
export const WORK_ACTION_BODY_MAX_BYTES = 64 * 1024;
// One the machine has not asked for within this is given up on: done an
// hour later, it would surprise whoever asked.
export const WORK_ACTION_QUEUED_TTL_MS = 60 * 60_000;
// One handed to the machine that it has not answered within this is taken
// to have been lost with it.
export const WORK_ACTION_SENT_TTL_MS = 10 * 60_000;
export const WORK_ACTION_ERROR_MAX = 300;
// How long a finished one is kept, and what the page reads of a member's.
export const WORK_ACTION_RETENTION_MS = 7 * 24 * 60 * 60_000;
export const MAX_WORK_ACTIONS_READ = 50;
/**
 * The most a machine can have been asked and not answered: what an hour of
 * WORK_ACTIONS_PER_HOUR queues, and the reports asked for besides.
 */
export const MAX_WORK_ACTIONS_WAITING = 2 * WORK_ACTIONS_PER_HOUR;
// Merged worktrees one "clean up all" asks for.
export const MAX_WORK_CLEANUP_BATCH = 50;
// How long a worktree can be snoozed for.
export const WORK_SNOOZE_MAX_MS = 90 * 24 * 60 * 60_000;

// ---- Cycles ----
// Issues not done yet one cycle holds, across the projects of its
// workspace. Planning past it is refused; it is what a cycle's page and its
// close read of each status.
export const MAX_CYCLE_ISSUES = 500;
// Cycles made ahead of the current one, so that the next can be planned.
export const CYCLES_AHEAD = 2;
// Unfinished issues a closed cycle moves to the next one per transaction.
export const ROLL_OVER_BATCH = 100;
// Cycles closed per run of the daily job, each in its own transaction.
export const CYCLES_CLOSED_PER_RUN = 20;
// Past cycles the cycles page and the stats list.
export const MAX_PAST_CYCLES_LISTED = 12;

// ---- Stats ----
// What the stats page reads at most: days of Work's time, and done issues.
export const MAX_STATS_ACTIVITY_READ = 8_000;
export const MAX_STATS_ISSUES_READ = 1_000;
// Done issues the stats page lists.
export const STATS_ISSUES_LISTED = 50;

// ---- Handoff to T3 Code ----
// Handoffs and tests one member may send an hour: each can start an agent.
export const HANDOFFS_PER_HOUR = 10;
// Handoffs of issues given to someone's agents, in an hour of their own: what
// teammates assign does not spend the owner's.
export const AGENT_HANDOFFS_PER_HOUR = 20;
// The owner's unread notifications about one issue read to see whether they
// were told already that their agent could not be handed it.
export const MAX_HANDOFF_FAILURES_LOOKED_AT = 200;
// How far back in an issue's log a handoff looks for the assignment it was
// scheduled for. It runs at once, so that assignment is among the last few.
export const HANDOFF_ASSIGNMENT_LOOKBACK = 100;
// The notifications about one ticket or issue an agent's mark_inbox_read
// reads: everyone's about a ticket, where each person has a handful, and the
// agent's own unread ones about an issue, which its subscribers fill.
export const MAX_NOTIFICATIONS_PER_SUBJECT = 500;
// How long T3 Code has to accept a request.
export const HANDOFF_TIMEOUT_MS = 15_000;
// Requests kept per member for them to look over, and how many Settings lists.
export const HANDOFF_DELIVERIES_KEPT = 50;
export const HANDOFF_DELIVERIES_LISTED = 10;
// How often an issue given to an agent is sent to its T3 Code before its
// owner is told it never started, and how long the first wait is: longer
// than T3 Code's relay holds a request, and doubled after each.
export const AGENT_HANDOFF_ATTEMPTS = 6;
export const AGENT_HANDOFF_RETRY_MS = 45 * 60_000;
// Issues the sweep looks at each time, and the agent's comments and the
// pull requests read on each to see whether it started.
export const AGENT_HANDOFFS_SWEPT = 25;
export const AGENT_HANDOFF_COMMENTS_READ = 50;

// ---- Experiments and ratings ----
// See docs/ratings-and-experiments.md, which several of these are from.
// A project's experiments, ended ones included: the Experiments page and the
// SDK's config read all of them at once, so creation refuses here.
export const MAX_EXPERIMENTS_PER_PROJECT = 100;
export const EXPERIMENT_NAME_MAX = 200;
// A description, and a hypothesis.
export const EXPERIMENT_TEXT_MAX = 2000;
export const MIN_VARIATIONS = 2;
export const MAX_VARIATIONS = 10;
export const VARIATION_NAME_MAX = 100;
// No plan of weights needs more, and it keeps the arithmetic exact.
export const MAX_VARIATION_WEIGHT = 1_000_000;
// What a variation's configuration, an assignment's context and an event's
// properties may each weigh, as JSON in UTF-8.
export const VARIATION_CONFIGURATION_MAX_BYTES = 4 * 1024;
export const SDK_CONTEXT_MAX_BYTES = 4 * 1024;
export const EVENT_PROPERTIES_MAX_BYTES = 4 * 1024;
// The body of a call to /api/v1/sdk/assign or /track: the largest context or
// properties, with room for the rest.
export const SDK_MAX_BODY_BYTES = 16 * 1024;
// A unit id (`visitorId`, `unitId`), as an app's own identifier may be.
export const UNIT_ID_MAX = 255;
// Event names one variation's totals keep. An event past them is refused:
// a stats row holds them all, and is read by the results page in one go.
export const MAX_EVENT_NAMES_PER_VARIATION = 50;
export const RATING_SUBJECT_ID_MAX = 255;
export const RATING_SUBJECT_VERSION_MAX = 50;
export const RATING_COMMENT_MAX = 2000;
// Experiment units one rating may name.
export const MAX_RATING_EXPERIMENTS = 10;
// Subject types the Ratings page offers as a filter.
export const MAX_RATING_SUBJECT_TYPES_LISTED = 50;
// Below this many ratings, a variation's numbers are said to be too few to read.
export const MIN_RATINGS_TO_READ = 30;

// ---- Project updates ----
// The words of one update: a few paragraphs, as long as a ticket's note.
export const PROJECT_UPDATE_BODY_MAX = 10_000;
// What a row of the inbox, or an agent's list, shows of one.
export const UPDATE_EXCERPT_MAX = 140;

// ---- Documents ----
// Specs and notes kept beside a project's work. A document's body is in a
// row of its own, so a page of the list reads titles and never bodies.
export const DOCUMENT_TITLE_MAX = 256;
// Characters of Markdown, about forty pages of text: a body row stays far
// below what Convex stores in one.
export const DOCUMENT_BODY_MAX = 100_000;
// Characters of a diagram's draw.io file, which spends most of them on
// markup: a page of forty boxes and their arrows is about 30,000. Four bytes
// a character at most keeps a body row under what Convex stores in one.
export const DIAGRAM_BODY_MAX = 200_000;
// Documents of one project. Counted when one is made, by reading the list.
export const MAX_DOCUMENTS_PER_PROJECT = 500;
// Identifiers (MOOD-142) of one document that are looked up to be links.
// Past these the rest stay text.
export const MAX_DOCUMENT_REFERENCES = 50;
// Documents the command menu finds by title.
export const MAX_DOCUMENT_SEARCH_RESULTS = 10;
